/**
 * Shipping Progress Bar — shipping-progress-bar.js
 *
 * Two complementary mechanisms keep the bar in sync with the live cart:
 *
 *  1. Dawn pub/sub  — subscribes to PUB_SUB_EVENTS.cartUpdate ('cart-update').
 *     Dawn publishes this event (with the full parsedState / cart object)
 *     after every AJAX cart mutation.  Fastest path.
 *
 *  2. MutationObserver — watches the <cart-items> custom element.
 *     When Dawn re-renders the cart section HTML after a quantity change
 *     or item removal, the DOM changes and the observer fires a debounced
 *     /cart.js fetch.  Reliable fallback that works even if pub/sub fires
 *     before our subscription is registered.
 *
 * Gift management:
 *   - Non-gift subtotal drives all threshold checks (gift price excluded).
 *   - Gift is auto-added / auto-removed; page reloads after each mutation.
 *   - A `giftMutationPending` flag prevents the observer / pub-sub from
 *     firing redundant fetches while a mutation is in flight.
 */
(function () {
  'use strict';

  var container = document.querySelector('.spb-container');
  if (!container) return;

  /* ── Config from Liquid data attributes ─────────────────── */
  var shippingThreshold = parseInt(container.dataset.shippingThreshold, 10) || 0;
  var giftEnabled       = container.dataset.giftEnabled === 'true';
  var giftThreshold     = parseInt(container.dataset.giftThreshold, 10) || 0;
  var giftProductId     = parseInt(container.dataset.giftProductId, 10) || 0;
  var giftVariantId     = parseInt(container.dataset.giftVariantId, 10) || 0;
  var discountCode      = (container.dataset.giftDiscount || '').trim();
  var maxCents          = parseInt(container.dataset.maxCents, 10) || shippingThreshold;

  var giftReady          = giftEnabled && giftProductId > 0 && giftVariantId > 0;
  var giftMutationPending = false;

  /* ── Helpers ─────────────────────────────────────────────── */

  function formatMoney(cents) {
    var d = cents / 100;
    return '$' + (d % 1 === 0 ? d : d.toFixed(2));
  }

  /* ── Visual update ───────────────────────────────────────── */

  function updateBarUI(nonGiftTotal) {
    var fillEl    = document.getElementById('spb-bar-fill');
    var messageEl = document.getElementById('spb-message');
    var iconEl    = document.getElementById('spb-icon');
    var msShip    = document.getElementById('spb-milestone-shipping');
    var msGift    = document.getElementById('spb-milestone-gift');

    var shippingReached = nonGiftTotal >= shippingThreshold;
    var giftReachedNow  = giftEnabled && nonGiftTotal >= giftThreshold;

    /* Fill width */
    if (fillEl && maxCents > 0) {
      var pct = Math.min((nonGiftTotal / maxCents) * 100, 100).toFixed(1);
      fillEl.style.width = pct + '%';
      fillEl.setAttribute('aria-valuenow', String(nonGiftTotal));
    }

    /* Milestone icon states */
    if (msShip) msShip.classList.toggle('is-reached', shippingReached);
    if (msGift) msGift.classList.toggle('is-reached', giftReachedNow);

    /* Status message + message-row icon */
    if (messageEl) {
      var msg = '';
      var iconType = '';

      if (giftReachedNow) {
        msg      = container.dataset.msgGiftAchieved || '';
        iconType = 'gift';
      } else if (shippingReached && giftEnabled) {
        var remGift = giftThreshold - nonGiftTotal;
        msg      = (container.dataset.msgGiftPending || '').replace('[amount]', formatMoney(remGift));
        iconType = 'gift';
      } else if (shippingReached) {
        msg      = container.dataset.msgShippingAchieved || '';
        iconType = 'check';
      } else {
        var remShip = shippingThreshold - nonGiftTotal;
        msg      = (container.dataset.msgShippingPending || '').replace('[amount]', formatMoney(remShip));
        iconType = 'truck';
      }

      if (msg) messageEl.textContent = msg;
      if (iconEl && iconType) iconEl.className = 'spb-icon spb-icon--' + iconType;
    }
  }

  /* ── Discount-code injection ─────────────────────────────── */

  function syncDiscountCode(shouldApply) {
    if (!discountCode) return;
    var btn = document.getElementById('checkout') || document.querySelector('[name="checkout"]');
    if (!btn) return;
    if (shouldApply) {
      btn.setAttribute('formaction', '/checkout?discount=' + encodeURIComponent(discountCode));
    } else {
      btn.removeAttribute('formaction');
    }
  }

  /* ── Gift add / remove ───────────────────────────────────── */

  function addGift() {
    if (giftMutationPending) return;
    giftMutationPending = true;
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: giftVariantId, quantity: 1, properties: { _spb_gift: 'true' } })
    })
      .then(function (r) { return r.json(); })
      .then(function () { window.location.reload(); })
      .catch(function (e) {
        giftMutationPending = false;
        console.error('[SPB] add gift failed', e);
      });
  }

  function removeGift(key) {
    if (giftMutationPending) return;
    giftMutationPending = true;
    fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
      .then(function (r) { return r.json(); })
      .then(function () { window.location.reload(); })
      .catch(function (e) {
        giftMutationPending = false;
        console.error('[SPB] remove gift failed', e);
      });
  }

  /* ── Core cart processor ─────────────────────────────────── */

  function processCart(cart) {
    var items = (cart && cart.items) ? cart.items : [];

    /* Locate gift line item */
    var giftLine = null;
    if (giftReady) {
      for (var i = 0; i < items.length; i++) {
        if (items[i].product_id === giftProductId) { giftLine = items[i]; break; }
      }
    }

    /* Non-gift subtotal */
    var nonGiftTotal = 0;
    for (var j = 0; j < items.length; j++) {
      if (giftReady && items[j].product_id === giftProductId) continue;
      nonGiftTotal += items[j].line_price;
    }

    updateBarUI(nonGiftTotal);

    if (!giftReady) return;

    var shouldHaveGift = nonGiftTotal >= giftThreshold;
    syncDiscountCode(shouldHaveGift);

    if (shouldHaveGift && !giftLine) {
      addGift();
    } else if (!shouldHaveGift && giftLine) {
      removeGift(giftLine.key);
    }
  }

  /* ── Fetch /cart.js and process ─────────────────────────── */

  function fetchAndProcess() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(processCart)
      .catch(function (e) { console.error('[SPB] cart fetch failed', e); });
  }

  /* ── Mechanism 1: Dawn pub/sub ───────────────────────────── */

  function setupPubSub() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (giftMutationPending) return;

      /* event.cartData is the parsed /cart/change.js response object */
      var cartData = event && event.cartData;
      if (cartData && Array.isArray(cartData.items)) {
        processCart(cartData);
      } else {
        fetchAndProcess();
      }
    });
  }

  /* ── Mechanism 2: MutationObserver on <cart-items> ──────────
     Dawn re-renders <cart-items> innerHTML on every AJAX cart
     change.  The observer detects this and triggers a /cart.js
     re-fetch with a 200 ms debounce to avoid redundant calls.
     ─────────────────────────────────────────────────────────── */

  function setupMutationObserver() {
    var cartEl = document.querySelector('cart-items');
    if (!cartEl) return;

    var debounceTimer;
    var observer = new MutationObserver(function () {
      if (giftMutationPending) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(fetchAndProcess, 200);
    });

    observer.observe(cartEl, { childList: true, subtree: true });
  }

  /* ── Init ────────────────────────────────────────────────── */

  function init() {
    fetchAndProcess();
    setupPubSub();
    setupMutationObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
